import { useEffect, useState } from "react";
import { FileDown, FileUp } from "lucide-react";
import client, { extractError } from "@/api";

const today = () => new Date().toISOString().slice(0, 10);

async function downloadDocument(document) {
  const response = await client.get(`/commercial-invoices/${document.id}/download`, {
    responseType: "blob",
  });
  const url = URL.createObjectURL(response.data);
  const anchor = window.document.createElement("a");
  anchor.href = url;
  anchor.download = document.file_name;
  anchor.click();
  URL.revokeObjectURL(url);
}

export const CommercialInvoicePanel = ({ transaction }) => {
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [form, setForm] = useState({
    invoice_number: "", invoice_date: today(), subtotal_amount: transaction.trade_value,
    gst_amount: 0, total_amount: transaction.trade_value, remarks: "", file: null,
  });

  useEffect(() => {
    let active = true;
    setLoading(true);
    client.get(`/commercial-invoices/by-transaction/${transaction.id}`)
      .then((response) => { if (active) setDocument(response.data); })
      .catch((err) => {
        if (active) setError(extractError(err, "Unable to load the commercial invoice."));
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [transaction.id]);

  if (!["COMPLETED", "DISPUTED"].includes(transaction.status)) return null;

  const update = (event) => {
    const { name, value, files } = event.target;
    setForm((current) => ({ ...current, [name]: files ? files[0] : value }));
  };
  const upload = async (event) => {
    event.preventDefault();
    setBusy(true); setError(""); setMessage("");
    try {
      const body = new FormData();
      Object.entries(form).forEach(([key, value]) => body.append(key, value));
      const response = await client.post(
        `/commercial-invoices/${transaction.id}/upload`, body,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setDocument(response.data);
      setMessage("Commercial invoice uploaded and shared with the buyer.");
    } catch (err) {
      setError(extractError(err, "Unable to upload the commercial invoice."));
    } finally {
      setBusy(false);
    }
  };
  const download = async () => {
    setError("");
    try { await downloadDocument(document); }
    catch (err) { setError(extractError(err, "Unable to download the commercial invoice.")); }
  };

  return (
    <section className="section-block" data-testid="commercial-invoice-panel">
      <div className="section-heading">
        <div>
          <p className="eyebrow">UPLOADED COMMERCIAL INVOICE</p>
          <h3>Seller-issued invoice document</h3>
        </div>
      </div>
      {error && <div className="form-error" data-testid="commercial-invoice-error">{error}</div>}
      {message && <div className="form-success" data-testid="commercial-invoice-success">{message}</div>}
      {loading ? <div className="loading-block" data-testid="commercial-invoice-loading">Loading…</div> : document ? (
        <ul className="finance-list" data-testid="commercial-invoice-summary">
          <li><span>Invoice number</span><strong data-testid="commercial-invoice-number">{document.invoice_number}</strong></li>
          <li><span>Invoice date</span><strong>{new Date(`${document.invoice_date}T00:00:00`).toLocaleDateString()}</strong></li>
          <li><span>Subtotal</span><strong>₹{document.subtotal_amount.toLocaleString("en-IN")}</strong></li>
          <li><span>GST</span><strong>₹{document.gst_amount.toLocaleString("en-IN")}</strong></li>
          <li className="total"><span>Total</span><strong>₹{document.total_amount.toLocaleString("en-IN")}</strong></li>
          <li><span>Uploaded by</span><strong data-testid="commercial-invoice-uploader">{document.uploaded_by_email || document.uploaded_by_user_id}</strong></li>
          <li><span>Uploaded</span><strong data-testid="commercial-invoice-uploaded-at">{new Date(document.created_at).toLocaleString()}</strong></li>
          {document.remarks && <li><span>Remarks</span><strong data-testid="commercial-invoice-remarks">{document.remarks}</strong></li>}
          <li><span>{document.file_name}</span><button type="button" className="primary-button" onClick={download} data-testid="commercial-invoice-download-button"><FileDown size={14} /> Download PDF</button></li>
        </ul>
      ) : transaction.viewer_side === "SELLER" ? (
        <form className="stack-form" onSubmit={upload} data-testid="commercial-invoice-upload-form">
          <div className="form-grid">
            <label>Invoice number<input name="invoice_number" value={form.invoice_number} onChange={update} required maxLength={80} data-testid="commercial-invoice-number-input" /></label>
            <label>Invoice date<input name="invoice_date" type="date" value={form.invoice_date} onChange={update} required data-testid="commercial-invoice-date-input" /></label>
            <label>Subtotal (₹)<input name="subtotal_amount" type="number" min="0" value={form.subtotal_amount} onChange={update} required data-testid="commercial-invoice-subtotal-input" /></label>
            <label>GST (₹)<input name="gst_amount" type="number" min="0" value={form.gst_amount} onChange={update} required data-testid="commercial-invoice-gst-input" /></label>
            <label>Total (₹)<input name="total_amount" type="number" min="0" value={form.total_amount} onChange={update} required data-testid="commercial-invoice-total-input" /></label>
            <label>Remarks (optional)<textarea name="remarks" rows={3} maxLength={1000} value={form.remarks} onChange={update} data-testid="commercial-invoice-remarks-input" /></label>
            <label>Invoice PDF<input name="file" type="file" accept="application/pdf,.pdf" onChange={update} required data-testid="commercial-invoice-file-input" /></label>
          </div>
          <button type="submit" className="primary-button" disabled={busy} data-testid="commercial-invoice-upload-button"><FileUp size={14} /> {busy ? "Uploading…" : "Upload commercial invoice"}</button>
        </form>
      ) : (
        <p className="muted" data-testid="commercial-invoice-awaiting-seller">The seller has not uploaded a commercial invoice yet.</p>
      )}
    </section>
  );
};